import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

export enum RecognitionType {
  RIDE_SUPPORTER = 'RIDE_SUPPORTER',
  RIDE_LEADER = 'RIDE_LEADER',
  COMMUNITY_AMBASSADOR = 'COMMUNITY_AMBASSADOR',
  SAFETY_CHAMPION = 'SAFETY_CHAMPION',
  MENTOR = 'MENTOR',
}

export enum RecognitionScope {
  STORE_SPECIFIC = 'STORE_SPECIFIC',
  GLOBAL = 'GLOBAL',
}

export interface UserRecognition {
  id: string;
  userId: string;
  storeId?: string | null;
  type: RecognitionType;
  scope: RecognitionScope;
  awardedBy: string;
  note?: string;
  createdAt: string;
  store?: {
    id: string;
    name: string;
    location?: string;
  };
  awarder?: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

const BadgesPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [recognitions, setRecognitions] = useState<UserRecognition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.id) {
      fetchBadges();
    }
  }, [user?.id]);

  const fetchBadges = async () => {
    if (!user?.id) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/badges/users/${user.id}/badges`);
      setRecognitions(response.data.recognitions || []);
    } catch (err: any) {
      console.error('Error fetching badges:', err);
      const errorMessage = err.message || t('profile.badgesError');
      if (err.response?.status === 404 || errorMessage.includes('not found')) {
        setRecognitions([]);
        setError(null);
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const getRecognitionImage = (type: RecognitionType): string => {
    switch (type) {
      case RecognitionType.RIDE_SUPPORTER:
        return '/badges/ride-supporter.png';
      case RecognitionType.RIDE_LEADER:
        return '/badges/ride-leader.png';
      case RecognitionType.COMMUNITY_AMBASSADOR:
        return '/badges/community-ambassador.png';
      case RecognitionType.SAFETY_CHAMPION:
        return '/badges/safety-champion.png';
      case RecognitionType.MENTOR:
        return '/badges/mentor.png';
      default:
        return '/badges/welcome.png';
    }
  };

  const getRecognitionLabel = (type: RecognitionType): string => {
    switch (type) {
      case RecognitionType.RIDE_SUPPORTER:
        return t('profile.badgeRideSupporter');
      case RecognitionType.RIDE_LEADER:
        return t('profile.badgeRideLeader');
      case RecognitionType.COMMUNITY_AMBASSADOR:
        return t('profile.badgeCommunityAmbassador');
      case RecognitionType.SAFETY_CHAMPION:
        return t('profile.badgeSafetyChampion');
      case RecognitionType.MENTOR:
        return t('profile.badgeMentor');
      default:
        return type;
    }
  };

  const getRecognitionColor = (type: RecognitionType): string => {
    switch (type) {
      case RecognitionType.RIDE_SUPPORTER:
        return 'bg-green-100 text-green-800 border-green-300';
      case RecognitionType.RIDE_LEADER:
        return 'bg-orange-100 text-orange-800 border-orange-300';
      case RecognitionType.COMMUNITY_AMBASSADOR:
        return 'bg-purple-100 text-purple-800 border-purple-300';
      case RecognitionType.SAFETY_CHAMPION:
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case RecognitionType.MENTOR:
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch {
      return dateString;
    }
  };

  // Group recognitions by scope
  const globalRecognitions = recognitions.filter(r => r.scope === RecognitionScope.GLOBAL);
  const storeRecognitions = recognitions.filter(r => r.scope === RecognitionScope.STORE_SPECIFIC);

  // Group store-specific recognitions by store
  const storeGroups = storeRecognitions.reduce((acc, rec) => {
    const storeName = rec.store?.name || t('profile.unknownStore');
    if (!acc[storeName]) {
      acc[storeName] = [];
    }
    acc[storeName].push(rec);
    return acc;
  }, {} as Record<string, UserRecognition[]>);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">
          <div className="text-lg">{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  if (recognitions.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/profile')}
          className="mb-4 text-primary-600 hover:text-primary-700"
        >
          ← {t('common.back')}
        </button>
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <div className="text-6xl mb-4">🏅</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('profile.badgesEmptyTitle')}</h2>
          <p className="text-gray-600">{t('profile.badgesEmptyMessage')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <button
        onClick={() => navigate('/profile')}
        className="mb-4 text-primary-600 hover:text-primary-700"
      >
        ← {t('common.back')}
      </button>

      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center space-x-4">
          <img
            src="/badges/welcome.png"
            alt="Badges"
            className="w-12 h-12 object-contain"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              const fallback = document.createElement('div');
              fallback.className = 'text-4xl';
              fallback.textContent = '🏅';
              target.parentNode?.insertBefore(fallback, target);
            }}
          />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('profile.badgesTitle')}</h1>
            <p className="text-gray-600">
              {recognitions.length} {recognitions.length === 1 ? t('profile.badge') : t('profile.badges')}
            </p>
          </div>
        </div>
      </div>

      {/* Global Recognitions */}
      {globalRecognitions.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('profile.globalRecognitions')}</h2>
          <div className="grid gap-4 md:grid-cols-2">
            {globalRecognitions.map((recognition) => (
              <div
                key={recognition.id}
                className={`bg-white rounded-lg shadow p-6 border-2 ${getRecognitionColor(recognition.type)}`}
              >
                <div className="flex items-start space-x-4">
                  <img
                    src={getRecognitionImage(recognition.type)}
                    alt={getRecognitionLabel(recognition.type)}
                    className="w-16 h-16 object-contain"
                    onError={(e) => {
                      // Fallback to emoji if image fails to load
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const fallback = document.createElement('div');
                      fallback.className = 'text-4xl';
                      fallback.textContent = '🏅';
                      target.parentNode?.insertBefore(fallback, target);
                    }}
                  />
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold mb-2">
                      {getRecognitionLabel(recognition.type)}
                    </h3>
                    <p className="text-sm opacity-75 mb-2">
                      {t('profile.awardedOn')} {formatDate(recognition.createdAt)}
                    </p>
                    {recognition.note && (
                      <p className="text-sm italic mb-2">{recognition.note}</p>
                    )}
                    <p className="text-xs opacity-75">
                      {t('profile.awardedBy')} RideConnect Staff
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Store-Specific Recognitions */}
      {Object.keys(storeGroups).length > 0 && (
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('profile.storeRecognitions')}</h2>
          {Object.entries(storeGroups).map(([storeName, recs]) => (
            <div key={storeName} className="mb-6">
              <h3 className="text-lg font-medium text-primary-600 mb-3">{storeName}</h3>
              <div className="grid gap-4 md:grid-cols-2">
                {recs.map((recognition) => (
                  <div
                    key={recognition.id}
                    className={`bg-white rounded-lg shadow p-6 border-2 ${getRecognitionColor(recognition.type)}`}
                  >
                    <div className="flex items-start space-x-4">
                      <img
                        src={getRecognitionImage(recognition.type)}
                        alt={getRecognitionLabel(recognition.type)}
                        className="w-16 h-16 object-contain"
                        onError={(e) => {
                          // Fallback to emoji if image fails to load
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const fallback = document.createElement('div');
                          fallback.className = 'text-4xl';
                          fallback.textContent = '🏅';
                          target.parentNode?.insertBefore(fallback, target);
                        }}
                      />
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold mb-2">
                          {getRecognitionLabel(recognition.type)}
                        </h3>
                        <p className="text-sm opacity-75 mb-2">
                          {formatDate(recognition.createdAt)}
                        </p>
                        {recognition.note && (
                          <p className="text-sm italic mb-2">{recognition.note}</p>
                        )}
                        <p className="text-xs opacity-75">
                          {t('profile.awardedBy')} RideConnect Staff
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BadgesPage;
